import { useApiClient } from "@/src/hooks/useApiClient";
import { useQueryErrorToast } from "@/src/hooks/useQueryErrorToast";
import { useTranslations } from "@/src/i18n/i18n";

/**
 * Les événements d'un compte : ceux qu'il a créés, ceux qu'il a rejoints.
 *
 * ⚠️ **Les deux ensembles sont disjoints** : `joined` **exclut** ce que le
 * compte a créé. Créer un événement y inscrit et y valide automatiquement
 * l'organisateur ; sans cette exclusion, deux créations et une participation
 * s'afficheraient « 2 créés, 3 rejoints », et le même événement serait compté
 * deux fois. Ne « corrige » pas ça en additionnant les deux listes.
 *
 * ⚠️ **Chaque liste est bornée à 100 par le serveur**, avec son total à côté :
 * `totalCreated` et `totalJoined` ne se comparent donc pas à la longueur des
 * tableaux, et c'est ce qui permet d'afficher « et 12 autres » plutôt que de
 * tout rapatrier. La route n'accepte **aucun filtre** — pour chercher, c'est
 * `/events?q=`.
 */
export function useAccountEvents(accountId: number | undefined) {
  const $api = useApiClient();
  const t = useTranslations("accounts");

  const { data, error, isError, isLoading } = $api.useQuery(
    "get",
    "/api/v1/moderation/accounts/{id}/events",
    { params: { path: { id: accountId ?? 0 } } },
    // La fiche s'ouvre avant que l'identifiant ne soit connu : sans ce garde,
    // une première requête partirait sur le compte `0`.
    { enabled: accountId !== undefined },
  );

  useQueryErrorToast(isError, t("events-error"), error);

  return {
    created: data?.data?.created ?? [],
    joined: data?.data?.joined ?? [],
    totalCreated: data?.data?.totalCreated ?? 0,
    totalJoined: data?.data?.totalJoined ?? 0,
    isLoading,
  };
}
